import type { AstroCookies } from "astro";
import i18next from "i18next";

// Supported languages for translations - exclude en as it's the default language
export type TranslatedLanguages = "fr" | "de" | "nl" | "es";
export type SupportedLanguage = "en" | TranslatedLanguages;
export const defaultLanguage = "en";
export const supportedLanugages = ["en", "fr", "de", "nl", "es"];
export const namespaces = [
  "common",
  "home",
  "privacy-policy",
  "terms-of-service",
  "valorant-boosting",
  "dashboard",
  "jobs",
  "lol",
  "booster",
  "blog",
  "accounts",
  "landing",
  "boosting",
  "manager",
  "games"
];
export const defaultNamespace = "common";
export const isValidLanguage = (lang: string): lang is TranslatedLanguages => {
  return supportedLanugages.includes(lang) && lang !== "en";
};

function getLanguageOrDefault(
  lang: string | null | undefined
): SupportedLanguage {
  return lang && isValidLanguage(lang) ? lang : defaultLanguage;
}

export function detectRequestLanguage(cookies: AstroCookies, headers: Headers) {
  const cookiePreference = getLanguageOrDefault(cookies.get("i18next")?.value);
  const headerPreference = getLanguageOrDefault(
    headers.get("accept-language")?.split(",")[0]
  );
  return cookiePreference || headerPreference;
}
export function getURLLanguageCode(url: URL): string | null {
  // Get first path segment and check if it's a supported language
  const firstSegment = url.pathname.split("/").filter(Boolean)[0];
  return firstSegment && supportedLanugages.includes(firstSegment)
    ? firstSegment
    : null;
}

export function localizeUrl(
  inputUrl: string,
  targetLang: string = i18next.language
): string {
  const hasProtocol = inputUrl.includes("://");
  const url = hasProtocol
    ? new URL(inputUrl)
    : new URL(inputUrl, "https://dummy.com");
  const urlLang = getURLLanguageCode(url) || defaultLanguage;

  // no need to localize
  if (urlLang === targetLang) {
    return hasProtocol ? url.toString() : url.pathname;
  }

  // Remove default language from path. example: https://playplex.com/en/test -> https://playplex.com/test
  // This should not happen but it's a fallback to guarantee the url is localized
  if (url.pathname.startsWith(`/${defaultLanguage}`)) {
    url.pathname = url.pathname.replace(`/${defaultLanguage}`, "");
  }

  // Case 1: Switching to default language - remove language segment
  if (targetLang === defaultLanguage) {
    url.pathname = url.pathname.replace(`/${urlLang}`, "");
  }
  // Case 2: Current URL has default language (implicit, not in path)
  else if (urlLang === defaultLanguage) {
    url.pathname = `/${targetLang}${url.pathname}`;
  }
  // Case 3: Switching between two non-default languages
  else {
    url.pathname = url.pathname.replace(`/${urlLang}`, `/${targetLang}`);
  }

  return hasProtocol ? url.toString() : url.pathname;
}

export function getLanguageRedirectResponse(
  url: URL,
  urlLang: string | null,
  preferredLang: string
) {
  const resources = /\.[a-zA-Z0-9]+$/;
  const endPoints = /^\/(auth|api|trpc|unsubscribe)/;
  const isResource = resources.test(url.pathname);
  const isEndPoint = endPoints.test(url.pathname);

  // Return null if it's a resource, endpoint, or if there's already a language code in the URL
  if (isResource || isEndPoint || urlLang) return null;

  // Only redirect if there's no language code and the preferred language isn't English
  if (!urlLang && preferredLang !== defaultLanguage) {
    return {
      status: 302,
      headers: { Location: `/${preferredLang}${url.pathname}` + url.search }
    };
  }

  return null;
}
